import { PriorPaymentInfoModalComponent } from '@1bill-app/components/prior-payment-info-modal/prior-payment-info-modal.component';
import { APP_CONFIG, ROUTE_SETTINGS_CONTACT_DETAILS } from '@1bill-app/constants';
import { environment } from '@1bill-app/env';
import { objDeepEquals } from '@1bill-app/helpers/object.helper';
import { BillService } from '@1bill-app/services/bill/bill.service';
import {
  PaymentCard,
  PaymentCardService,
} from '@1bill-app/services/payment/payment-card.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import { Settings, SettingsOptions } from '@1bill-app/services/settings/settings.store';
import { UIService } from '@1bill-app/services/ui.service';
import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { Component, Input, NgZone, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertController,
  IonItemSliding,
  LoadingController,
  ModalController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { showError } from 'src/app/services/custom-functions/custom-functions';
import { HomeService } from '@1bill-app/services/home/home.service';
import { phoneNumberIsVerified } from '@1bill-app/helpers/auth.helper';

declare var $: any;

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.page.html',
  styleUrls: ['./payment-information.page.scss'],
  animations: getListAnimation(),
})
export class PaymentInformationPage implements OnInit {
  @Input() profileStrength = 0;
  // @Input() state: PaymentCardState;
  // card: PaymentCard;
  loading: HTMLIonLoadingElement;
  saving: boolean;
  accountSettings: Settings;
  subs = new SubSink();
  filterStatus = { status: 'A' };
  accountCards$ = this.query.accountCards$;
  paymentInstForm: FormGroup;
  isFormUntouched: boolean = true;
  isSafariDesktop: boolean;
  checkError: any;
  checkWarning: any;
  public settingsData: Settings;
  isDesktop$ = this.uiService.isDesktopView().pipe(map((isDesktop) => isDesktop));

  get anyCards$() {
    return this.accountCards$.pipe(map((cards) => cards?.length > 0));
  }

  get anyBankAccounts$() {
    return this.yodleeQuery.select('accounts').pipe(map((accounts) => accounts?.length > 0));
  }

  constructor(
    private alertCtr: AlertController,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    private settingsService: SettingsService,
    public query: SettingsQuery,
    private paymentCardService: PaymentCardService,
    private route: ActivatedRoute,
    private loadingController: LoadingController,
    private logger: NGXLogger,
    private router: Router,
    private billService: BillService,
    private zone: NgZone,
    public platform: Platform,
    private yodleeQuery: YodleeQuery,
    private homeService: HomeService,
    private uiService: UIService,
  ) {
    this.initForm();
  }

  initForm() {
    this.paymentInstForm = new FormGroup({
      paymentMethod: new FormControl('', Validators.required),
    });
  }

  handleLinkBankAccount() {
    this.homeService.presentLinkBankAccountModal();
  }

  async showSuccess(message: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: APP_CONFIG.TOAST_SUCCESS_DURATION,
      cssClass: ['toast-error'],
    });
    await toast.present();
  }

  async ngOnInit() {
    // this.loading = await this.loadingController.create({
    //   message: 'Loading settings data...',
    // });
    // this.loading.present();
    this.fetchData();
    this.query.select().subscribe({
      next: (settingsData) => {
        this.settingsData = settingsData;

        let initialFormValues = {
          paymentMethod: settingsData.paymentMethod,
        };
        this.paymentInstForm.setValue(initialFormValues);

        this.paymentInstForm.valueChanges.subscribe((result) => {
          // this.logger.log('form change results:', result);
          if (objDeepEquals(initialFormValues, result) || !this.paymentInstForm.valid) {
            this.isFormUntouched = true;
          } else {
            // this.logger.log('value changed');
            this.isFormUntouched = false;
          }
        });
      },
      error: (err) => {
        this.logger.error('unable to retrieve user setting from data store', err);
      },
    });
    this.route.queryParams.subscribe((p) => {
      this.logger.log('payment listener received params:', p);
      if (p?.CallbackStatus) {
        this.paymentTokenListener(p);
      }
    });
    // this.subs.sink = this.query.select().subscribe({
    //   next: (data) => {
    //     this.accountSettings = data;
    //   },
    //   error: (err) => {
    //     this.logger.error('unable to retrieve user setting from data store', err);
    //   },
    // });
  }

  async fetchData(fetchBill = false) {
    const loading = await this.loadingController.create({
      message: 'Loading settings data...',
    });
    await loading.present();
    if (fetchBill) {
      //fetch bill to show update in payment info
      this.subs.sink = this.billService.fetchBill().subscribe();
    }
    this.subs.sink = this.settingsService.fetch().subscribe({
      next: async (data) => {
        this.accountSettings = data;
        this.isFormUntouched = true;
        // this.logger.log('user settings data fetched:', JSON.stringify(r));
        this.logger.log('dismiss loading controller', this.accountSettings);
        await loading.dismiss();
      },
      error: async (e) => {
        await loading.dismiss();
        this.logger.error('unable to fetch user settings data', e);
      },
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  selectPaymentMethod(event: any) {
    if (event?.detail?.value === 'A' && phoneNumberIsVerified()) {
      this.paymentInstForm.get('paymentMethod').setValue('M');
      this.presentActionDeniedModal();
    }
  }

  private async presentActionDeniedModal() {
    await this.uiService.presentActionDeniedModal(
      'You must verify your mobile number',
      'before selecting this option.',
      'Verify mobile number',
      ROUTE_SETTINGS_CONTACT_DETAILS,
      null,
      null,
      ['payment-info-action-denied'],
    );
  }

  // getCardMasked() {
  //   const cardType = this.card && this.card.CardType;
  //   let cardNumber = this.card && this.card.CardNumber;
  //   if (!cardType || !cardNumber) {
  //     return;
  //   }
  //   cardNumber = cardNumber[0] + cardNumber.slice(1).replace(/.(?!$)/g, 'x');
  //   return `${cardType} ${cardNumber}`;
  // }

  async presentProfileCompletionModal() {
    const modal = await this.modalCtrl.create({
      component: PriorPaymentInfoModalComponent,
      cssClass: 'modal--popup-prior-payment',
    });
    await modal.present();
    return;
  }

  async setToDefault(card: PaymentCard, slidingItem: IonItemSliding) {
    slidingItem.closeOpened();
    const alert = await this.alertCtr.create({
      // cssClass: 'my-custom-class',
      header: 'Set this card as default card for payment?',
      // subHeader: 'Set this card to default?',
      message: card.cardType + ' ' + card.cardNumber,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          // cssClass: 'secondary',
          handler: (blah) => {
            this.logger.log('Confirm Cancel: blah');
          },
        },
        {
          text: 'Set to default',
          cssClass: 'alert-accept',
          handler: (blah) => {
            this.logger.log('Confirm set to default: blah');
            this.onSetDefault(card);
          },
        },
      ],
    });

    await alert.present();
  }

  async removeCard(card: PaymentCard, slidingItem: IonItemSliding, lastCard = false) {
    slidingItem.closeOpened();
    const alert = await this.alertCtr.create({
      // cssClass: 'my-custom-class',
      header: 'Remove this card?',
      message: lastCard
        ? 'If you remove all your cards, all your bills will be set to manual payment.'
        : null,
      subHeader: card.cardType + ' ' + card.cardNumber,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          // cssClass: 'secondary',
          handler: (blah) => {
            this.logger.log('Confirm Cancel: blah');
          },
        },
        {
          text: 'Remove',
          cssClass: 'alert-accept',
          handler: (blah) => {
            this.logger.log('Confirm Remove: blah');
            this.onRemove(card, lastCard);
          },
        },
      ],
    });

    await alert.present();
  }

  async paymentTokenListener(p: any) {
    if (p.Token && p.CallbackStatus && p.CallbackStatus.toLocaleLowerCase() === 'successful') {
      this.loading = await this.loadingController.create({
        message: 'Updating card details...',
      });
      await this.loading.present();
      try {
        const response = await this.paymentCardService.save(p).toPromise();
        if (response?.success === false) {
          showError('Card details already added.');
        }
      } finally {
        await this.fetchData();
        await this.loading.dismiss();
      }
      this.router.navigate(['.'], { relativeTo: this.route });
    }
  }

  async onPaymentInstructionChange() {
    //Check if payment card is added
    if (!this.settingsData.defaultAccountCardId) {
      showError('Please add payment information to save payment instructons.');
      return;
    }

    this.loading = await this.loadingController.create({
      message: 'Saving payment instruction...',
    });
    this.loading.present();
    try {
      this.logger.debug(this.paymentInstForm.value);
      const formData = {
        ...this.paymentInstForm.value,
      };

      // STAGE: ready for save
      this.logger.debug(formData);

      if (formData.paymentMethod === 'A') {
        this.presentActionDeniedModal();
        return;
      }

      this.saving = true;
      const data: Settings = formData;
      const payload: SettingsOptions = { option: 'PAYMENT', settingsData: data };
      await this.settingsService.saveSettingDetails(payload).toPromise();
      this.showSuccess('Settings saved successfully.');
      this.fetchData(true);
      this.isFormUntouched = true;
    } catch (err) {
      if (!environment.production) {
        console.error(err);
      }
      // send error log to server
      this.logger.error('onPaymentInstructionChange', err);
      // display error toast
      showError('Error occurred while saving settings data!!');
    } finally {
      this.loading.dismiss();
    }
    this.saving = false;
  }

  // fetchPaymentCard() {
  //   this.subs.sink = this._paymentCard.get().subscribe();
  // }

  async onSetDefault(card: PaymentCard) {
    const loading = await this.loadingController.create({
      message: 'Updating default card...',
    });
    await loading.present();
    try {
      if (!environment.production) {
        this.logger.log('update payment cards');
        this.logger.debug('accountSettings', this.accountSettings);
      }
      this.saving = true;
      await this.paymentCardService.updatePaymentCard(card).toPromise();
      // This will force change detection in UI for native app.
      this.zone.run(async () => {
        await this.fetchData();
      });
      await loading.dismiss();
    } catch (err) {
      if (!environment.production) {
        console.error(err);
      }
      // send error log to server
      this.logger.error('onSetDefault', err);
      await loading.dismiss();
      // display error toast
      showError('Error occurred while saving settings data!!', true);
    }
    this.saving = false;
  }

  async onRemove(card: PaymentCard, lastCard) {
    this.loading = await this.loadingController.create({
      message: 'Removing card...',
    });
    this.loading.present();
    try {
      // STAGE: ready for remove
      if (!environment.production) {
        this.logger.log('remove payment cards');
        this.logger.debug('accountSettings', this.accountSettings);
      }
      this.saving = true;
      await this.paymentCardService.removePaymentCard(card).toPromise();
      this.loading.dismiss();
      await this.fetchData(lastCard);
    } catch (err) {
      if (!environment.production) {
        console.error(err);
      }
      // send error log to server
      this.logger.error('onRemove card', err);
      this.loading.dismiss();
      // display error toast
      showError('Error occurred while saving settings data!!', true);
    }
    this.saving = false;
  }
  trackByFn(index: number, item: PaymentCard) {
    return item.accountPaymentId;
  }
  getCardBrandLogoImg(cardType: string) {
    if (!cardType) return null;
    switch (cardType.toLowerCase()) {
      case 'visa':
        return '/assets/brands/Visa-Card-logos75x56-Visa.png';
      case 'mastercard':
        return '/assets/brands/MasterCard-Card-logos75x56-MC.png';
      case 'american express':
        return '/assets/brands/American_Express-Card-logos75x56-Amex.png';
      default:
        return null;
    }
  }
  onPaymentMethodClick() {
    this.homeService.openZenithAddPaymentCard();
  }
}
export function getListAnimation() {
  return [
    trigger('listAnimation', [
      transition('* => *', [
        // each time the binding value changes
        query(
          ':leave',
          [
            stagger(100, [
              animate('0.5s', style({ opacity: 0, transform: 'translateY(-8px)' })),
            ]),
          ],
          {
            optional: true,
          },
        ),
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(-8px)' }),
            stagger(100, [animate('0.5s', style({ opacity: 1, transform: 'none' }))]),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ];
}
